import { useEffect, useState } from 'react';
import type { LookAtThatCursor } from '../../state/lookAtThatStore';

interface Props {
  screenshot: string | null;
  width: number | null;
  height: number | null;
  status: string;
  lastAction: string | null;
  analyzing: boolean;
  cursor: LookAtThatCursor | null;
  typing: boolean;
  adapter?: string | null;
}

/**
 * Live viewer. With the CDP screencast the image updates several times per
 * second; the virtual cursor glides to wherever the bot is about to act
 * (coordinates arrive just before each click/scroll/drag executes).
 */
export function LookAtThatViewer({
  screenshot,
  width,
  height,
  status,
  lastAction,
  analyzing,
  cursor,
  typing,
  adapter,
}: Props) {
  // Retrigger the click ripple on every new action, even at the same spot.
  const [rippleSeq, setRippleSeq] = useState(0);
  useEffect(() => {
    if (cursor?.kind === 'click' || cursor?.kind === 'drag') setRippleSeq(cursor.seq);
  }, [cursor?.seq, cursor?.kind]);

  const frameW = width || 1440;
  const frameH = height || 900;
  const cursorStyle = cursor
    ? {
        left: `${Math.min(100, Math.max(0, (cursor.x / frameW) * 100))}%`,
        top: `${Math.min(100, Math.max(0, (cursor.y / frameH) * 100))}%`,
      }
    : undefined;

  return (
    <div className="card look-at-that-viewer">
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Game view</h3>
        <span className="spacer" />
        {adapter ? <span className="badge small">{adapter}</span> : null}
        <span className="muted small">{status}</span>
      </div>
      <div className="look-at-that-frame">
        {screenshot ? (
          <>
            <img
              src={screenshot.startsWith('data:') ? screenshot : `data:image/png;base64,${screenshot}`}
              alt="Live game screenshot"
              className="look-at-that-shot"
            />
            {cursor && (
              <div className="look-at-that-cursor" style={cursorStyle} data-kind={cursor.kind}>
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                  <path
                    d="M5 3l14 8.5-6.2 1.2L16 19l-2.8 1.4-3.2-6.3L5 18z"
                    fill="#fff"
                    stroke="#111"
                    strokeWidth="1.4"
                  />
                </svg>
                {(cursor.kind === 'click' || cursor.kind === 'drag') && (
                  <span key={rippleSeq} className="look-at-that-ripple" />
                )}
                {cursor.kind === 'scroll' && <span className="look-at-that-cursor-tag">scroll</span>}
                {cursor.kind === 'drag' && <span className="look-at-that-cursor-tag">drag</span>}
              </div>
            )}
            {typing && (
              <div className="look-at-that-typing">
                <span className="look-at-that-typing-dot" />
                typing…
              </div>
            )}
            {analyzing && (
              <div className="look-at-that-overlay look-at-that-overlay-corner">
                <span>AI thinking…</span>
              </div>
            )}
          </>
        ) : (
          <div className="look-at-that-empty muted">
            Start a session to stream the live game here.
          </div>
        )}
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <span className="muted small">{lastAction ? `Last: ${lastAction}` : '—'}</span>
        <span className="spacer" />
        <span className="muted small mono">
          {width && height ? `${width} × ${height}` : ''}
        </span>
      </div>
    </div>
  );
}
